import React, { useState, useEffect } from 'react';
import { getConsentGraphics, createConsentGraphic, deleteConsentGraphic, updateConsentGraphicStatus, updateConsentGraphicName } from '../../servicios/apiClinica';
import Modal from '../../comun/Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const GraficosConsentimiento = () => {
    const [graphics, setGraphics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [name, setName] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    
    const [editGraphicId, setEditGraphicId] = useState(null);
    const [editGraphicName, setEditGraphicName] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);

    const fetchGraphics = async () => {
        setLoading(true);
        try {
            const data = await getConsentGraphics();
            setGraphics(data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching graphics:', err);
            setError('Error al cargar la lista de gráficos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGraphics();
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('Por favor ingrese un nombre.');
            return;
        }
        if (!imageFile) {
            alert('Por favor seleccione una imagen.');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('image', imageFile);
            formData.append('is_active', 'true');

            await createConsentGraphic(formData);
            
            // Reset form
            setName('');
            setImageFile(null);
            setImagePreview(null);
            const fileInput = document.getElementById('imageFile');
            if (fileInput) fileInput.value = '';
            
            fetchGraphics();
            alert('Gráfico guardado correctamente.');
        } catch (err) {
            console.error(err);
            alert('Error al guardar el gráfico.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este gráfico?')) return;
        
        try {
            await deleteConsentGraphic(id);
            fetchGraphics();
            alert('Gráfico eliminado.');
        } catch (err) {
            console.error(err);
            alert('Error al eliminar el gráfico.');
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            await updateConsentGraphicStatus(id, !currentStatus);
            fetchGraphics();
        } catch (err) {
            console.error(err);
            alert('Error al actualizar el estado del gráfico.');
        }
    };

    const handleEditName = (id, currentName) => {
        setEditGraphicId(id);
        // Pre-fill with uppercase so they don't have to retype if they just want it uppercase
        setEditGraphicName((currentName || '').toUpperCase());
        setShowEditModal(true);
    };

    const saveEditName = async () => {
        if (!editGraphicName.trim()) {
            alert('El nombre no puede estar vacío.');
            return;
        }
        
        setSavingEdit(true);
        try {
            await updateConsentGraphicName(editGraphicId, editGraphicName.toUpperCase());
            fetchGraphics();
            setShowEditModal(false);
        } catch (err) {
            console.error(err);
            alert('Error al actualizar el nombre del gráfico.');
        } finally {
            setSavingEdit(false);
        }
    };

    const styles = {
        mainContent: {
            padding: '2rem',
            backgroundColor: '#f8fafc',
            minHeight: '100vh',
            fontFamily: 'Inter, system-ui, sans-serif'
        },
        header: {
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            padding: '2rem',
            borderRadius: '16px',
            marginBottom: '2rem',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        },
        title: { margin: 0, fontSize: '1.75rem', fontWeight: '700' },
        card: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '2rem'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1.5rem',
            marginTop: '1.5rem'
        },
        graphicCard: {
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center',
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative'
        },
        image: {
            width: '80px',
            height: '80px',
            objectFit: 'contain',
            marginBottom: '0.5rem'
        },
        deleteBtn: {
            position: 'absolute',
            top: '5px',
            right: '5px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px'
        },
        editBtn: {
            position: 'absolute',
            top: '5px',
            right: '35px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px'
        }
    };

    return (
        <div style={styles.mainContent}>
            <div style={styles.header}>
                    <h1 style={styles.title}>Gráficos de Consentimiento Informado</h1>
                    <p style={{ margin: '0.5rem 0 0', color: '#cbd5e1' }}>Gestione las imágenes que aparecerán en la sección de Consentimiento Informado (Fase 12)</p>
                </div>

                <div style={styles.card}>
                    <h3 style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>Añadir Nuevo Gráfico</h3>
                    <form onSubmit={handleUpload} className="row g-3 align-items-end">
                        <div className="col-md-5">
                            <label className="form-label fw-bold">Nombre del Gráfico (Ej. Profilaxis)</label>
                            <input 
                                type="text" 
                                className="form-control" 
                                value={name} 
                                onChange={e => setName(e.target.value.toUpperCase())} 
                                placeholder="Nombre..." 
                                required
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label fw-bold">Seleccionar Imagen (.jpg, .png)</label>
                            <input 
                                type="file" 
                                id="imageFile"
                                className="form-control" 
                                accept="image/*"
                                onChange={handleFileChange} 
                                required
                            />
                        </div>
                        <div className="col-md-2">
                            <button type="submit" className="btn btn-primary w-100 fw-bold" disabled={uploading}>
                                {uploading ? 'Subiendo...' : 'Subir Gráfico'}
                            </button>
                        </div>
                    </form>
                    
                    {imagePreview && (
                        <div className="mt-3 text-center p-3 bg-light rounded" style={{ maxWidth: '200px' }}>
                            <p className="mb-2 text-muted small fw-bold">Vista Previa:</p>
                            <img src={imagePreview} alt="Preview" style={{ width: '100%', height: 'auto', maxHeight: '100px', objectFit: 'contain' }} />
                        </div>
                    )}
                </div>

                <div style={styles.card}>
                    <h3 style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>Gráficos Existentes</h3>
                    
                    {error && <div className="alert alert-danger">{error}</div>}
                    
                    {loading ? (
                        <p className="mt-3 text-muted">Cargando gráficos...</p>
                    ) : (
                        <div style={styles.grid}>
                            {graphics.map(g => (
                                <div key={g.id} style={{ ...styles.graphicCard, opacity: g.is_active ? 1 : 0.5, backgroundColor: g.is_active ? '#fff' : '#f1f5f9' }}>
                                    <button style={styles.deleteBtn} onClick={() => handleDelete(g.id)} title="Eliminar">
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                    <button style={styles.editBtn} onClick={() => handleEditName(g.id, g.name)} title="Editar Nombre">
                                        <i className="bi bi-pencil-fill"></i>
                                    </button>
                                    <div className="form-check form-switch position-absolute" style={{ top: '5px', left: '10px' }}>
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            role="switch" 
                                            checked={g.is_active} 
                                            onChange={() => handleToggleStatus(g.id, g.is_active)}
                                            title={g.is_active ? "Desactivar Gráfico" : "Activar Gráfico"}
                                        />
                                    </div>
                                    <img 
                                        src={g.image.startsWith('http') ? g.image : `${API_URL}${g.image}`} 
                                        alt={g.name} 
                                        style={styles.image} 
                                    />
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginTop: 'auto' }}>
                                        {g.name} {!g.is_active && <span className="text-danger">(Inactivo)</span>}
                                    </div>
                                </div>
                            ))}
                            
                            {graphics.length === 0 && (
                                <div className="text-muted w-100 mt-3" style={{ gridColumn: '1 / -1' }}>No hay gráficos registrados.</div>
                            )}
                        </div>
                    )}
                </div>
                
            <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Editar Nombre del Gráfico" maxWidth="400px">
                <div className="form-group mb-3">
                    <label className="fw-bold mb-2">Nombre del Gráfico</label>
                    <input 
                        type="text" 
                        className="form-control" 
                        value={editGraphicName}
                        onChange={(e) => setEditGraphicName(e.target.value.toUpperCase())}
                        placeholder="Ingrese el nombre"
                    />
                    <small className="text-muted mt-2 d-block">El nombre se forzará a mayúsculas automáticamente.</small>
                </div>
                <div className="d-flex justify-content-end mt-4">
                    <button className="btn btn-secondary me-2" onClick={() => setShowEditModal(false)} disabled={savingEdit}>
                        Cancelar
                    </button>
                    <button className="btn btn-primary" onClick={saveEditName} disabled={savingEdit}>
                        {savingEdit ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default GraficosConsentimiento;
